import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Edit3, Loader2, Mail, ShieldCheck } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { containsTemplatePlaceholder, htmlToPlainText, plainTextToHtml, sanitizeEmailHtml } from "@/lib/email-client-helpers";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type Template = { id: string; code: string; libelle: string; relectureObligatoire: boolean };
type Availability = { disponible: boolean; destinataire: string | null; donneesManquantes: string[] };
type Preview = { objet: string; corps: string; destinataire: string | null; relectureObligatoire: boolean; donneesManquantes: string[] };
export type TicketClientEmailOpenRequest = { id: number; modeleCode: string };

export function TicketClientEmail({
  ticketId,
  openRequest,
}: {
  ticketId: string;
  openRequest?: TicketClientEmailOpenRequest | null;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState("");
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [original, setOriginal] = useState({ subject: "", body: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const lock = useRef(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const autoFocusBodyPending = useRef(false);
  const [autoEditCode, setAutoEditCode] = useState<string | null>(null);
  const modeles = useQuery<{ modeles: Template[]; disponibilite: Availability }>({
    queryKey: [`/api/tickets/${ticketId}/emails/modeles`],
    queryFn: async () => {
      const res = await authFetch(`/api/tickets/${ticketId}/emails/modeles`);
      if (!res.ok) throw new Error("Impossible de charger les modèles d’email");
      return res.json();
    },
    enabled: !!ticketId,
  });
  const templates = modeles.data?.modeles || [];
  const availability = modeles.data?.disponibilite;
  const selected = templates.find((item) => item.code === selectedCode);
  const preview = useQuery<Preview>({
    queryKey: [`/api/tickets/${ticketId}/emails/modeles/${selectedCode}/apercu`],
    queryFn: async () => {
      const res = await authFetch(`/api/tickets/${ticketId}/emails/modeles/${selectedCode}/apercu`);
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || "Impossible de préparer l’aperçu");
      return payload;
    },
    enabled: open && !!selectedCode && !!selected?.relectureObligatoire,
  });
  const missing = preview.data?.donneesManquantes?.length ? preview.data.donneesManquantes : (availability?.donneesManquantes || []);
  const isEdited = subject !== original.subject || body !== original.body;
  const invalidPlaceholder = containsTemplatePlaceholder(subject) || containsTemplatePlaceholder(body);
  const editedContentEmpty = isEdited && (!subject.trim() || !body.trim());
  const previewUnavailable = !!selected?.relectureObligatoire && (preview.isError || (!preview.isLoading && !preview.data));
  const freeResponseUnchanged = selectedCode === "reponse_libre"
    && body.replace(/\s+/g, " ").trim() === original.body.replace(/\s+/g, " ").trim();

  useEffect(() => {
    if (!open || !selected) return;
    if (!selected.relectureObligatoire) {
      setSubject(""); setBody(""); setOriginal({ subject: "", body: "" }); setEditing(false);
    }
  }, [open, selected]);
  useEffect(() => {
    if (!preview.data) return;
    const next = { subject: preview.data.objet, body: htmlToPlainText(preview.data.corps) };
    setSubject(next.subject); setBody(next.body); setOriginal(next);
    setEditing(autoEditCode === selectedCode);
  }, [preview.data, autoEditCode, selectedCode]);
  useEffect(() => {
    if (!openRequest || templates.length === 0) return;
    if (!templates.some(template => template.code === openRequest.modeleCode)) return;
    setError("");
    setAutoEditCode(openRequest.modeleCode);
    autoFocusBodyPending.current = openRequest.modeleCode === "reponse_libre";
    setOpen(true);
    setEditing(false);
    setSubject("");
    setBody("");
    setOriginal({ subject: "", body: "" });
    setSelectedCode(openRequest.modeleCode);
  }, [openRequest, templates]);
  useEffect(() => {
    if (
      !editing
      || selectedCode !== "reponse_libre"
      || !autoFocusBodyPending.current
    ) return;
    autoFocusBodyPending.current = false;
    const frame = requestAnimationFrame(() => {
      const textarea = bodyRef.current;
      if (!textarea) return;
      const blankParagraph = body.indexOf("\n\n");
      const position = blankParagraph >= 0 ? blankParagraph + 1 : body.length;
      textarea.focus();
      textarea.setSelectionRange(position, position);
    });
    return () => cancelAnimationFrame(frame);
  }, [editing, selectedCode]);

  if (modeles.isLoading || modeles.isError || templates.length === 0) return null;
  const unavailable = availability?.disponible === false;
  const openPanel = () => { setError(""); setOpen(true); if (!selectedCode) setSelectedCode(templates[0].code); };
  const selectTemplate = (code: string) => {
    setError("");
    setEditing(false);
    setSubject("");
    setBody("");
    setOriginal({ subject: "", body: "" });
    setSelectedCode(code);
  };
  const changeOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) return;
    setSelectedCode("");
    setSubject("");
    setBody("");
    setOriginal({ subject: "", body: "" });
    setEditing(false);
    setAutoEditCode(null);
    autoFocusBodyPending.current = false;
    setError("");
  };
  const send = async () => {
    if (freeResponseUnchanged) {
      setError("Rédigez votre réponse avant l'envoi");
      return;
    }
    if (
      lock.current
      || unavailable
      || !selectedCode
      || missing.length
      || invalidPlaceholder
      || editedContentEmpty
      || previewUnavailable
      || (!!selected?.relectureObligatoire && preview.isFetching)
    ) return;
    lock.current = true; setSending(true); setError("");
    try {
      const payload: Record<string, string> = { modeleCode: selectedCode };
      if (isEdited) { payload.objet = subject; payload.corps = plainTextToHtml(body); }
      const res = await authFetch(`/api/tickets/${ticketId}/emails`, { method: "POST", body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Erreur ${res.status}`);
      await queryClient.invalidateQueries({ queryKey: ["/api/tickets/messages"] });
      setOpen(false); setSelectedCode(""); setSubject(""); setBody(""); setOriginal({ subject: "", body: "" }); setEditing(false);
      toast({ title: "Email envoyé au client", description: "L’envoi a été ajouté au fil de discussion." });
    } catch (e) { setError(e instanceof Error ? e.message : "Échec de l’envoi"); }
    finally { lock.current = false; setSending(false); }
  };
  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button variant="outline" size="sm" onClick={openPanel} disabled={unavailable} data-testid="button-send-client-email">
                <Mail className="h-4 w-4 mr-2" />Envoyer un email
              </Button>
            </span>
          </TooltipTrigger>
          {unavailable && <TooltipContent>Envoi indisponible : {availability?.donneesManquantes.join(", ")}</TooltipContent>}
        </Tooltip>
        {unavailable && (
          <Button variant="ghost" size="sm" onClick={openPanel} data-testid="button-preview-client-email">
            Consulter l’aperçu
          </Button>
        )}
      </div>
      <Dialog open={open} onOpenChange={changeOpen}>
        <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="dialog-client-email">
          <DialogHeader><DialogTitle>Envoyer un email au client</DialogTitle><DialogDescription>Vérifiez le modèle et le destinataire avant l’envoi.</DialogDescription></DialogHeader>
          {missing.length > 0 && <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="email-missing-data"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>Données manquantes : {missing.join(", ")}</span></div>}
          {error && <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert" data-testid="email-send-error">{error}</div>}
          <div className="space-y-4">
            <div><Label htmlFor="email-template">Modèle</Label><Select value={selectedCode || undefined} onValueChange={selectTemplate}><SelectTrigger id="email-template" data-testid="select-email-template"><SelectValue placeholder="Sélectionner un modèle" /></SelectTrigger><SelectContent>{templates.map((item) => <SelectItem key={item.code} value={item.code}>{item.libelle}</SelectItem>)}</SelectContent></Select></div>
            <div><Label htmlFor="email-recipient">Destinataire</Label><Input id="email-recipient" value={preview.data?.destinataire || availability?.destinataire || ""} readOnly data-testid="input-email-recipient" /></div>
            {selected?.relectureObligatoire && preview.isLoading && <div className="h-28 animate-pulse rounded-md bg-muted" data-testid="email-preview-loading" />}
            {selected?.relectureObligatoire && preview.isError && <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">Impossible de charger l’aperçu. L’envoi est désactivé.</div>}
            {selected?.relectureObligatoire && preview.data && !editing && <div className="space-y-3 rounded-md border bg-muted/20 p-4" data-testid="email-preview"><div><Label>Objet</Label><p className="font-medium">{subject}</p></div><div><Label>Corps</Label><div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(isEdited ? plainTextToHtml(body) : preview.data.corps) }} /></div></div>}
            {selected?.relectureObligatoire && preview.data && <Button type="button" variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="button-edit-client-email"><Edit3 className="h-4 w-4 mr-2" />{editing ? "Voir l’aperçu" : "Modifier"}</Button>}
            {editing && <div className="space-y-3"><div><Label htmlFor="email-subject">Objet</Label><Input id="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="input-email-subject" /></div><div><Label htmlFor="email-body">Corps</Label><Textarea ref={bodyRef} id="email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={10} data-testid="textarea-email-body" /></div>{invalidPlaceholder && <p className="text-sm text-destructive" role="alert">Les marqueurs {"{{...}}"} ne sont pas autorisés dans un email modifié.</p>}{editedContentEmpty && <p className="text-sm text-destructive" role="alert">L’objet et le corps de l’email doivent être renseignés.</p>}</div>}
            {!selected?.relectureObligatoire && selected && <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"><ShieldCheck className="inline h-4 w-4 mr-1" />Ce modèle ne nécessite pas de relecture.</div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => changeOpen(false)} disabled={sending}>Annuler</Button><Button onClick={send} disabled={!!missing.length || unavailable || !selectedCode || sending || invalidPlaceholder || editedContentEmpty || previewUnavailable || (selected?.relectureObligatoire && preview.isFetching)} data-testid="button-confirm-client-email">{sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{!sending && <CheckCircle2 className="h-4 w-4 mr-2" />}{sending ? "Envoi en cours…" : "Envoyer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}